+<template>
  <div class="d-flex flex-column px-6 py-4" style="height: 100%">
    <DueDatePicker
      :isEditMode="isEditMode"
      :dueDateTime="rev.dueDate"
      @change="handleSetDueDate"
    />
    <div class="d-flex flex-column mb-4" v-if="!isEditMode && priority">
      <FieldLabel class="mt-1" :text="$t('common.priority')" />
      <div class="mt-2">
        <v-chip
          v-show="priority === 'low'"
          color="light-blue lighten-4"
          text-color="light-blue darken-4"
        >
          {{ $t('rev.priority.low') }}
        </v-chip>
        <v-chip
          v-show="priority === 'medium'"
          color="amber lighten-4"
          text-color="brown darken-1"
        >
          {{ $t('rev.priority.medium') }}
        </v-chip>
        <v-chip
          v-show="priority === 'high'"
          color="red lighten-4"
          text-color="red darken-4"
        >
          {{ $t('rev.priority.high') }}
        </v-chip>
      </div>
    </div>
    <div class="d-flex flex-column mb-4" v-if="isEditMode">
      <v-select
        v-model="priority"
        flat
        dense
        outlined
        hide-details
        :items="priorities"
        :label="$t('common.priority')"
        :placeholder="$t('common.select')"
      ></v-select>
    </div>
    <template v-if="hasChallengeActive">
      <ChallengeAddon
        :location="rev.location"
        :canEdit="canEdit"
        :revType="modelName"
        :revId="modelId"
        @onChallengeChange="onChallengeChange"
      />
    </template>

    <div data-testid="rev_view_location_attributes" class="mb-4">
      <LocationSelector
        v-if="isEditMode"
        verticalLayout
        :organizationId="revOrgId"
        :location="initialLocation"
        @change="onLocationChange"
      />
      <div v-else>
        <FieldLabel :text="$t('common.location')" />
        <div class="mb-4">
          <v-chip
            data-private
            v-if="rev.location.site.name"
            class="label mt-2 me-2"
            outlined
          >
            <v-icon size="16" color="blue-grey darken-2" class="me-1"
              >mdi-warehouse</v-icon
            >

            {{ rev.location.site.name }}
          </v-chip>
          <v-chip
            data-private
            v-if="rev.location.site.country"
            class="label mt-2"
            outlined
          >
            <v-icon size="16" color="blue-grey darken-2" class="me-1"
              >mdi-earth</v-icon
            >
            {{ $t(`countries.${rev.location.site.countryCodes[0]}`) }}
          </v-chip>
        </div>

        <template v-if="revTags && revTags.length">
          <FieldLabel class="d-block" :text="$t('common.attribute_other')" />
          <v-chip
            data-private
            v-for="attr in revTags"
            class="label me-2 mt-2"
            outlined
            :key="attr.id"
          >
            {{ attr.name }}
          </v-chip>
        </template>
      </div>
    </div>
    <div v-if="!isEditMode" class="d-flex flex-column justify-self-end">
      <FieldLabel :text="$t('common.created_on')" />

      <p data-private class="label ma-0 pa-0">
        {{ toHumanDistanceToNow(rev.createdAt) }}
      </p>

      <div v-if="revAuthor" class="d-flex flex-column mt-4">
        <FieldLabel :text="`${$t('common.created_by')}:`" />
        <ChipAvatar small :account="revAuthor" />
      </div>

      <div v-if="isReplica" class="d-flex align-start mt-4">
        <v-icon color="primary" size="16" class="me-2">
          mdi-information-outline
        </v-icon>
        <p
          v-html="
            $t('rev.replication.replica_rev_about_label', {
              revLink: linkToOriginalRev,
            })
          "
          class="caption ma-0"
        />
      </div>

      <div v-if="hasBeenReplicated" class="d-flex align-start mt-4">
        <v-icon color="primary" size="16" class="me-2">
          mdi-content-duplicate
        </v-icon>
        <p
          v-html="
            $t('rev.replication.total_replicas_label', {
              count: rev.totalReplicas,
            })
          "
          class="caption ma-0"
        />
      </div>

      <div
        v-if="hasFinding"
        class="d-flex align-start mt-4"
      >
        <v-icon color="primary" size="16" class="me-2">
          mdi-clipboard-text-outline
        </v-icon>
        <p class="caption ma-0">
          {{ $t('rev.finding.notice') }}
          <a
            href="#"
            class="text-decoration-underline"
            @click.prevent="openFindingDrawer"
          >
            {{ $t('rev.finding.view_link') }}
          </a>
        </p>
      </div>

      <template v-if="rev.completedAt">
        <FieldLabel class="mt-4" :text="$t('common.completed_on')" />
        <p data-private class="label pa-0 ma-0">
          {{ toHumanDistanceToNow(rev.completedAt) }}
        </p>
      </template>

      <template v-if="implementedValidation.isValidated">
        <FieldLabel
          class="mt-4"
          :text="$t(`rev.validation.implementation_validated_on`)"
        />
        <p class="label ma-0 pa-0">
          {{ toHumanDistanceToNow(implementedValidation.validatedAt) }}
        </p>
        <FieldLabel
          class="mt-4"
          :text="$t('rev.validation.implementation_validated_by')"
        />
        <ChipAvatar small :account="implementedValidation.validatedBy" />
      </template>

      <template v-if="financialValidation.isValidated">
        <FieldLabel
          class="mt-4"
          :text="$t(`rev.validation.impact_validated_on`)"
        />
        <p class="label ma-0 pa-0">
          {{ toHumanDistanceToNow(financialValidation.validatedAt) }}
        </p>

        <FieldLabel
          class="mt-4"
          :text="$t(`rev.validation.impact_validated_by`)"
        />
        <ChipAvatar small :account="financialValidation.validatedBy" />
      </template>

      <template v-if="validatedReview.hasUpdatedValidation">
        <FieldLabel
          class="mt-4"
          :text="$t(`rev.validation.last_validated_on`)"
        />
        <p class="label ma-0 pa-0">
          {{ toHumanDistanceToNow(validatedReview.lastValidatedOn) }}
        </p>

        <FieldLabel
          class="mt-4"
          :text="$t(`rev.validation.last_validated_by`)"
        />
        <ChipAvatar small :account="validatedReview.lastValidatedBy" />
      </template>

      <template v-if="hasApprovals">
        <FieldLabel class="mt-4" :text="$t('rev.approved_by')" />
        <div class="d-flex flex-wrap">
          <div
            class="me-2"
            v-for="(approval, index) in rev.approvals"
            :key="index"
          >
            <ChipAvatar small :account="approval.approvedBy" />
            <p class="label mt-1">
              {{ toHumanDistanceToNow(approval.approvedAt) }}
            </p>
          </div>
        </div>
      </template>

      <FindingDrawer
        v-if="isFindingDrawerOpen && hasFinding"
        readOnly
        :showRedirectionAlert="false"
        :visible="isFindingDrawerOpen"
        :findingId="findingId"
        @close="closeFindingDrawer"
      />
    </div>
  </div>
</template>

<script>
import { utils } from 'frontend-js-shared'

import userMixin from '@/mixins/userMixin.vue'
import revMixin from '@/mixins/revMixin.vue'

import FieldLabel from '@/components/Core/FieldLabel'
import ChallengeAddon from '@/components/Rev/ChallengeAddon.vue'
import DueDatePicker from '@/components/DueDatePicker/DueDatePicker.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import LocationSelector from '@/components/LocationSelector/LocationSelector.vue'
import FindingDrawer from '@/components/FindingDrawer/FindingDrawer.vue'

import _ from 'lodash'

export default {
  name: 'RevSummary',

  mixins: [userMixin, revMixin],

  components: {
    FieldLabel,
    ChallengeAddon,
    DueDatePicker,
    ChipAvatar,
    LocationSelector,
    FindingDrawer,
  },

  props: {
    rev: {
      type: Object,
      required: true,
      default() {
        return {}
      },
    },
    errors: {
      type: Object,
      required: false,
      default() {
        return {}
      },
    },
    isEditMode: {
      type: Boolean,
      required: true,
      default: false,
    },
    canEdit: {
      type: Boolean,
      required: true,
      default: false,
    },
    hasChallengeActive: {
      type: Boolean,
      required: false,
      default: false,
    },
    modelType: {
      type: String,
      required: false,
    },
    modelName: {
      type: String,
      required: false,
    },
    modelId: {
      type: String,
      required: true,
      default: '',
    },
  },

  data() {
    return {
      isFindingDrawerOpen: false,
      priorities: [
        {
          value: 'high',
          text: this.$t('rev.priority.high'),
        },
        {
          value: 'medium',
          text: this.$t('rev.priority.medium'),
        },
        {
          value: 'low',
          text: this.$t('rev.priority.low'),
        },
        {
          value: null,
          text: this.$t('rev.priority.none'),
        },
      ],
    }
  },

  computed: {
    revAuthor() {
      return this.rev?.author ?? this.rev?.owner
    },
    validation() {
      return this.rev?.validations ?? this.rev?.validation ?? null
    },

    revOrgId() {
      return (
        this.rev?.organization?._id || this.rev?.location?.organization?._id
      )
    },

    initialLocation() {
      return {
        site:
          this.rev?.location?.site?.id ??
          this.rev?.location?.site?._id ??
          this.rev?.location?.site,
        tags: this.rev?.location?.tags,
      }
    },

    financialValidation() {
      return {
        isValidated: Boolean(
          this.validation?.financial?.isValidated ?? this.validation?.financial
        ),
        validatedAt:
          this.validation?.financialValidatedAt ??
          this.validation?.financial?.validatedAt,
        validatedBy:
          this.validation?.financialValidatedBy ??
          this.validation?.financial?.auditor,
      }
    },

    implementedValidation() {
      const validation = {
        isValidated: Boolean(
          this.validation?.implementation?.isValidated ??
            this.validation?.implementation
        ),
        validatedAt:
          this.validation?.implementationValidatedAt ??
          this.validation?.implementation?.validatedAt,
        validatedBy:
          this.validation?.implementationValidatedBy ??
          this.validation?.implementation?.auditor,
      }
      return validation
    },

    validatedReview() {
      return {
        hasUpdatedValidation: Boolean(this.rev?.validatedReview?.lastUpdatedBy),
        lastValidatedOn: this.rev?.validatedReview?.updatedAt,
        lastValidatedBy: this.rev?.validatedReview?.lastUpdatedBy,
      }
    },

    priority: {
      get() {
        return this.rev?.priority || null
      },
      set(priority) {
        this.$emit('priorityChanged', priority)
      },
    },

    hasApprovals() {
      return !_.isEmpty(this.rev?.approvals)
    },

    revTags() {
      return this.rev?.location?.tags
    },

    hasFinding() {
      return Boolean(this.rev?.findingId)
    },

    findingId() {
      return this.rev?.findingId ?? ''
    },

    isReplica() {
      return Boolean(this.rev?.replicatedFromRevId)
    },

    hasBeenReplicated() {
      return (this.rev?.totalReplicas ?? 0) > 0
    },

    revType() {
      switch (this.modelName) {
        case 'revs':
          return 'rev'
        case 'quick-actions':
          return 'quickAction'
        case 'problem-solving':
          return 'problemSolving'
        default:
          return ''
      }
    },

    linkToOriginalRev() {
      return this.isReplica
        ? this.generateRevRoute(
            this.revOrgId,
            this.revType,
            this.rev?.replicatedFromRevId
          )
        : ''
    },
  },

  methods: {
    onLocationChange(attrs) {
      this.$emit('onLocationChange', attrs)
    },

    onChallengeChange() {
      this.$emit('onChallengeChange')
    },

    toHumanDistanceToNow(date) {
      return utils.dates.toHumanDistanceToNow(date)
    },

    handleSetDueDate(date) {
      this.$emit('onDueDateChange', date)
    },

    openFindingDrawer() {
      this.isFindingDrawerOpen = true
    },

    closeFindingDrawer() {
      this.isFindingDrawerOpen = false
    },
  },
}
</script>
